import { createSlice } from '@reduxjs/toolkit'
import type { PayloadAction } from '@reduxjs/toolkit'

import { FORM_FIELDS } from '@/pages/getRecipe/microLayering/materialAdvice/utils/formTypes'

import { PLACEMENT } from './appSlice'
import { CrownInput, StumpShadeInput } from './stainingStudio'

interface StumpShadeStainingRecipe {
  [PLACEMENT.LEFT]: {
    [key: string]: {
      input: CrownInput
      output: any
    }
  }
  [PLACEMENT.RIGHT]: {
    [key: string]: {
      input: CrownInput
      output: any
    }
  }
}

interface stainingRecipe {
  [PLACEMENT.LEFT]: {
    [key: string]: {
      input: CrownInput
      output: any
    }
  }
  [PLACEMENT.RIGHT]: {
    [key: string]: {
      input: CrownInput
      output: any
    }
  }
}

export interface fullMonolithicState {
  materialAdviceActiveStep: number
  materialAdviceForm: { [key in keyof FormData]?: any }
  selectedMaterialPreparationKey: string
  selectedMaterialTargetKey: string
  materialPreparationTargetColor: any
  materialTargetColor: any
  materialAdviceOuput: any
  stumpShadeTargetColor: any
  stumpShadePreparationColor: any
  stumpSahdeStainingBrand: string
  stumpShadeRecipeOutput: any
  selectedStumpShadeBrand: string | null
  selectedStumpShadeKey: string | null
  stumpShadeRecipe: StumpShadeStainingRecipe | null
  selectedStainingKey: string | null
  stainingRecipe: stainingRecipe | null
  selectedStainingBrand: string | null
  frameworkRecipeType: string
}

const initialState: fullMonolithicState = {
  materialAdviceActiveStep: 0,
  materialAdviceForm: {
    [FORM_FIELDS.availspace_CERVICAL]: 0.5,
    [FORM_FIELDS.availspace_INCISAL]: 0.5,
    [FORM_FIELDS.availspace_MIDDLE]: 0.5,
  },
  selectedMaterialPreparationKey: '',
  selectedMaterialTargetKey: '',
  materialPreparationTargetColor: {},
  materialTargetColor: {},
  materialAdviceOuput: {},
  frameworkRecipeType: 'Single Zone',

  //Stump Shade
  stumpSahdeStainingBrand: '',
  stumpShadeTargetColor: {},
  stumpShadePreparationColor: {},
  stumpShadeRecipeOutput: {},

  //Stump Shade Staining
  selectedStumpShadeBrand: null,
  selectedStumpShadeKey: null,
  stumpShadeRecipe: {
    [PLACEMENT.LEFT]: {},
    [PLACEMENT.RIGHT]: {},
  },

  //Staining
  selectedStainingKey: null,
  stainingRecipe: {
    [PLACEMENT.LEFT]: {},
    [PLACEMENT.RIGHT]: {},
  },
  selectedStainingBrand: null,
}

export const fullMonolithicSlice = createSlice({
  name: 'fullMonolithic',
  initialState,
  reducers: {
    setMonolithicMaterialRecipeActiveStep: (
      state,
      action: PayloadAction<number>,
    ) => {
      return {
        ...state,
        materialAdviceActiveStep: action.payload,
      }
    },
    updateMonolithicMaterialAdviceValue: (
      state,
      action: PayloadAction<{ name: keyof FormData; value: any }>,
    ) => {
      return {
        ...state,
        materialAdviceForm: {
          ...state.materialAdviceForm,
          [action.payload.name]: action.payload.value,
        },
      }
    },
    setSelectedMonolithicMaterialPreparationKey: (
      state,
      action: PayloadAction<string>,
    ) => {
      state.selectedMaterialPreparationKey = action.payload
    },
    setSelectedMonolithicMaterialTargetKey: (
      state,
      action: PayloadAction<string>,
    ) => {
      state.selectedMaterialTargetKey = action.payload
    },
    setMonolithicMaterialPreparationTargetColor: (
      state,
      action: PayloadAction<{
        imageId: string
        targetcolor: (number | null | string)[]
        position: 'cervical' | 'middle' | 'incisal'
        imagePosition: { x: number; y: number }
      }>,
    ) => {
      const { targetcolor, position, imagePosition } = action.payload

      state.materialPreparationTargetColor[position] = {
        targetcolor,
        imagePosition,
      }
    },
    setMonolithicMaterialTargetColor: (
      state,
      action: PayloadAction<{
        imageId: string
        targetcolor: (number | null | string)[]
        position: 'cervical' | 'middle' | 'incisal'
        imagePosition: { x: number; y: number }
      }>,
    ) => {
      const { targetcolor, position, imagePosition } = action.payload

      state.materialTargetColor[position] = {
        targetcolor,
        imagePosition,
      }
    },
    setMonolithicMaterialAdviceOuput: (state, action) => {
      const { imageId, result } = action.payload

      if (!state.materialAdviceOuput) {
        state.materialAdviceOuput = {}
      }
      if (result === undefined) {
        delete state.materialAdviceOuput[imageId]
      } else {
        state.materialAdviceOuput[imageId] = {
          ...result,
          generatedAt: Date.now(),
          expiresAt: Date.now() + 24 * 60 * 60 * 1000,
        }
      }
    },

    resetFrameworkRecipeState: (
      state,
      action: PayloadAction<{ imageId: string }>,
    ) => {
      const { imageId } = action.payload

      delete state.materialAdviceOuput[imageId]
      state.materialPreparationTargetColor = {}
      state.materialTargetColor = {}
      state.materialAdviceForm = initialState.materialAdviceForm
      state.materialAdviceActiveStep = initialState.materialAdviceActiveStep

      state.selectedMaterialPreparationKey =
        initialState.selectedMaterialPreparationKey
      state.selectedMaterialTargetKey = initialState.selectedMaterialTargetKey
    },
    setFrameworkRecipeType: (state, action) => {
      state.frameworkRecipeType = action.payload
    },

    //Stump Shade
    setStumpShadeStainingBrand: (state, action: PayloadAction<string>) => {
      state.stumpSahdeStainingBrand = action.payload
    },
    setStumpShadePreparationTarget: (
      state,
      action: PayloadAction<{
        imageId: string
        targetcolor: (number | null | string)[]
        position: 'cervical' | 'middle' | 'incisal'
        imagePosition: { x: number; y: number }
      }>,
    ) => {
      const { imageId, targetcolor, position, imagePosition } = action.payload

      if (!state.stumpShadePreparationColor[imageId]) {
        state.stumpShadePreparationColor[imageId] = {}
      }

      state.stumpShadePreparationColor[imageId][position] = {
        targetcolor,
        imagePosition,
      }
    },
    setStumpShadeTargetColor: (
      state,
      action: PayloadAction<{
        imageId: string
        targetcolor: (number | null | string)[]
        position: 'cervical' | 'middle' | 'incisal'
        imagePosition: { x: number; y: number }
      }>,
    ) => {
      const { imageId, targetcolor, position, imagePosition } = action.payload

      if (!state.stumpShadeTargetColor[imageId]) {
        state.stumpShadeTargetColor[imageId] = {}
      }

      state.stumpShadeTargetColor[imageId][position] = {
        targetcolor,
        imagePosition,
      }
    },
    setStumpShadeRecipeOutput: (state, action) => {
      const { imageId, key, result } = action.payload

      if (!state.stumpShadeRecipeOutput) {
        state.stumpShadeRecipeOutput = {}
      }

      if (!state.stumpShadeRecipeOutput[imageId]) {
        state.stumpShadeRecipeOutput[imageId] = {}
      }

      if (result === undefined) {
        delete state.stumpShadeRecipeOutput[imageId][key]
      } else {
        state.stumpShadeRecipeOutput[imageId][key] = result
      }
    },
    removeStumpShadeTargetColor: (state, action) => {
      const { imageId, key } = action.payload
      delete state.stumpShadeTargetColor?.[imageId]?.[key]
    },
    removeStumpShadePreparationColor: (state, action) => {
      const { imageId, key } = action.payload
      delete state.stumpShadePreparationColor?.[imageId]?.[key]
    },

    //Stump Sahde Staining
    setSelectedStumpShadeStainingBrand: (
      state,
      action: PayloadAction<string | null>,
    ) => {
      return {
        ...state,
        selectedStumpShadeBrand: action.payload,
      }
    },
    setSelectedStumpShadeKey: (state, action: PayloadAction<string | null>) => {
      return {
        ...state,
        selectedStumpShadeKey: action.payload,
      }
    },
    setStumpShadeRecipe: (
      state,
      action: PayloadAction<{
        placement: PLACEMENT
        key: string
        data: {
          input?: StumpShadeInput
          output?: any
        }
      }>,
    ) => {
      const placement = action.payload
        .placement as unknown as keyof StumpShadeInput
      const currentRecipe = state.stumpShadeRecipe || {
        [PLACEMENT.LEFT]: {},
        [PLACEMENT.RIGHT]: {},
      }

      return {
        ...state,
        stumpShadeRecipe: {
          ...currentRecipe,
          [placement]: {
            ...currentRecipe[placement],
            [action.payload.key]: {
              ...currentRecipe[placement][action.payload.key],
              ...action.payload.data,
            },
          },
        },
      }
    },
    removeStumpShadeRecipeKey: (
      state,
      action: PayloadAction<{
        key: string
      }>,
    ) => {
      if (state?.stumpShadeRecipe?.[PLACEMENT.LEFT]) {
        delete state.stumpShadeRecipe[PLACEMENT.LEFT][action.payload.key]
      }
      if (state?.stumpShadeRecipe?.[PLACEMENT.RIGHT]) {
        delete state.stumpShadeRecipe[PLACEMENT.RIGHT][action.payload.key]
      }
    },
    clearFullmonolithicStumpShadeStainingState: () => initialState,

    //Staining
    setSelectedStainingKey: (state, action: PayloadAction<string | null>) => {
      return {
        ...state,
        selectedStainingKey: action.payload,
      }
    },
    setSelectedStainingBrand: (state, action: PayloadAction<string | null>) => {
      return {
        ...state,
        selectedStainingBrand: action.payload,
      }
    },
    setStainingRecipe: (
      state,
      action: PayloadAction<{
        placement: PLACEMENT
        key: string
        data: {
          input?: CrownInput
          output?: any
        }
      }>,
    ) => {
      const placement = action.payload.placement as keyof stainingRecipe
      const currentRecipe = state.stainingRecipe || {
        [PLACEMENT.LEFT]: {},
        [PLACEMENT.RIGHT]: {},
      }
      const expiresAt = Date.now() + 24 * 60 * 60 * 1000

      return {
        ...state,
        stainingRecipe: {
          ...currentRecipe,
          [placement]: {
            ...currentRecipe[placement],
            [action.payload.key]: {
              ...currentRecipe[placement][action.payload.key],
              ...action.payload.data,
              expiresAt,
            },
          },
        },
      }
    },
    clearFMStainingState: () => initialState,
    removeStainingKey: (
      state,
      action: PayloadAction<{
        key: string
      }>,
    ) => {
      if (state?.stainingRecipe?.[PLACEMENT.LEFT]) {
        delete state.stainingRecipe[PLACEMENT.LEFT][action.payload.key]
      }
      if (state?.stainingRecipe?.[PLACEMENT.RIGHT]) {
        delete state.stainingRecipe[PLACEMENT.RIGHT][action.payload.key]
      }
    },
  },
})

export const {
  setMonolithicMaterialRecipeActiveStep,
  updateMonolithicMaterialAdviceValue,
  setSelectedMonolithicMaterialPreparationKey,
  setSelectedMonolithicMaterialTargetKey,
  setMonolithicMaterialPreparationTargetColor,
  setMonolithicMaterialTargetColor,
  setMonolithicMaterialAdviceOuput,
  resetFrameworkRecipeState,
  setFrameworkRecipeType,

  //Stump shade
  setStumpShadePreparationTarget,
  setStumpShadeTargetColor,
  setStumpShadeStainingBrand,
  setStumpShadeRecipeOutput,
  removeStumpShadeTargetColor,
  removeStumpShadePreparationColor,

  //Stump Shade Staining
  setSelectedStumpShadeStainingBrand,
  setSelectedStumpShadeKey,
  setStumpShadeRecipe,
  removeStumpShadeRecipeKey,
  clearFullmonolithicStumpShadeStainingState,

  //Staining
  setSelectedStainingKey,
  setSelectedStainingBrand,
  setStainingRecipe,
  clearFMStainingState,
  removeStainingKey,
} = fullMonolithicSlice.actions
export default fullMonolithicSlice.reducer
